import * as React from 'react';
import { useRef, useMemo } from 'react';

/** Material UI Packages. */
import MuiAppBar from '@mui/material/AppBar';
import { styled, createTheme, ThemeProvider, Box, Toolbar, CssBaseline } from '@mui/material';

/** Internal Components. */
import Menubar from './Menubar';
import Dashboard from '../pages/Dashboard/Dashboard';
import AccountSetting from '../pages/AccountSetting/AccountSetting';
import Account from '../pages/Account/Account';
import Addons from '../pages/AddOns/Addons';
import MyProfile from '../pages/MyProfile/MyProfile';

/** CSS Style. */
const theme = createTheme({
    palette: {
        primary: {
            light: '#80b2bf',
            main: '#00647e',
            dark: '#003a52',
        },
        secondary: { main: '#c1c1c1' },
        info: {
            main: '#58a000',
        },
        warning: {
            main: '#f48704',
        },
        error: {
            main: '#d92936',
        },
    },
    typography: {
        fontFamily: 'NotoSans',
        fontWeightRegular: 400,
        fontWeightBold: 600,
        fontSize: 12,
    },
    MuiDataGrid: {
        styleOverrides: {
            root: {
                '& .MuiDataGrid-main > div:nth-of-type': {
                    height: 'word-wrap !important',
                },
            },
            row: {
                '&.Mui-selected': {
                    backgroundColor: 'white',
                    '&:hover': {
                        backgroundColor: 'white',
                    },
                },
            },
        },
    },
});

/** Width of the drawer */
const drawerWidth = 180;

/** Styling for the AppBar */
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: '100%',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

export default function Main(props) {
    /** Get props from App.js.  */
    const { dashboard, accountsetting, account, addons, myprofile } = props;
    /** Reference for detecting scroll position */
    const ref = useRef();

    /** Function to render different components based on conditions. */
    const PageComponent = (props) => {
        const { dashboard, accountsetting, account, addons, myprofile } = props;

        switch (true) {
            case dashboard:
                return <Dashboard />;
            case accountsetting:
                return <AccountSetting />;
            case account:
                return <Account />;
            case addons:
                return <Addons />;
            case myprofile:
                return <MyProfile />;
            default:
                return null;
        }
    };

    /** Memoize the PageComponent to avoid unnecessary re-renders. */
    const PageComponentMemo = useMemo(() => PageComponent, [dashboard, accountsetting, account, addons, myprofile]);

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: 'flex', bgcolor: '#80b2bf' }}>
                <CssBaseline />
                <AppBar position="absolute" sx={{ boxShadow: 'none' }}>
                    <Menubar />
                </AppBar>
                <Box
                    ref={ref}
                    className="main"
                    sx={{
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                        flexGrow: 1,
                        height: '100vh',
                        overflow: 'auto',
                    }}
                >
                    <Toolbar
                        id="scroller"
                        sx={{
                            minHeight: 52,
                        }}
                        variant="dense"
                    />
                    <Box>
                        <PageComponentMemo
                            dashboard={dashboard}
                            accountsetting={accountsetting}
                            account={account}
                            addons={addons}
                            myprofile={myprofile}
                        />
                    </Box>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
